import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Editor from '@monaco-editor/react';
import { useQuery } from '@tanstack/react-query';
import { useState, useRef, useMemo, useCallback, useEffect, useContext } from 'react';
import { Button, ModalWindow, Select, Table, useLocalizeMessage, useToastNotification, } from 'libs.nucleus';
import { ApiClientService, AuthContext, useApiClient } from 'libs.react_internal';
import { StudiesContext } from '../../../contexts';
export const AddEditDimensionModal = ({ isOpen, onClose, onSave, dataView, isLoading = false, isEditMode = false, }) => {
    const translate = useLocalizeMessage();
    const toast = useToastNotification();
    const { entityId } = useContext(AuthContext);
    const { studies } = useContext(StudiesContext);
    const webClient = useApiClient(ApiClientService.WEB);
    const momClient = useApiClient(ApiClientService.MOM);
    const editorRef = useRef(null);
    const monacoRef = useRef(null);
    const [sqlQuery, setSqlQuery] = useState('');
    const [queryResult, setQueryResult] = useState([]);
    const [isRunningQuery, setIsRunningQuery] = useState(false);
    const hasAutoRunRef = useRef(false);
    // Local state for study and environment selection (for preview only)
    const [selectedStudy, setSelectedStudy] = useState(undefined);
    const [selectedEnvironment, setSelectedEnvironment] = useState(undefined);
    const studyOptions = useMemo(() => studies.map((study) => ({
        label: study.name,
        value: study.id.toString(),
    })), [studies]);
    const fetchEnvironments = useCallback(async () => {
        if (!selectedStudy?.value) {
            return [];
        }
        try {
            const { data: response } = await momClient.get(`/v1/entities/${entityId}/studies/${selectedStudy.value}/environments`, {
                params: {
                    clusterTypes: 'dev',
                    withCortexAccess: true,
                },
            });
            return response.data.filter((env) => !env.deployment.isProd);
        }
        catch (error) {
            return [];
        }
    }, [selectedStudy?.value, entityId, momClient]);
    const { data: environments = [], isLoading: isLoadingEnvironments } = useQuery({
        queryKey: ['entities', entityId, 'studies', selectedStudy?.value, 'environments'],
        queryFn: fetchEnvironments,
        enabled: !!entityId && !!selectedStudy?.value,
        refetchOnWindowFocus: false,
    });
    const environmentOptions = useMemo(() => environments.map((env) => ({
        label: env.name,
        value: env.cortexId || env.id.toString(),
    })), [environments]);
    // Reset environment when study changes
    const handleStudyChange = useCallback((option) => {
        setSelectedStudy(option);
        setSelectedEnvironment(undefined);
    }, []);
    const handleEnvironmentChange = useCallback((option) => {
        setSelectedEnvironment(option);
    }, []);
    // Load SQL query when opening in edit mode
    useEffect(() => {
        if (isOpen) {
            if (isEditMode && dataView.data?.sql) {
                setSqlQuery(dataView.data.sql);
                hasAutoRunRef.current = false;
            }
            else if (!isEditMode) {
                setSqlQuery('');
                hasAutoRunRef.current = false;
            }
            setQueryResult([]);
            // Reset study/environment selection when modal opens
            setSelectedStudy(undefined);
            setSelectedEnvironment(undefined);
        }
        else {
            hasAutoRunRef.current = false;
        }
    }, [isOpen, isEditMode, dataView.data?.sql]);
    const handleEditorDidMount = (editor, monaco) => {
        editorRef.current = editor;
        monacoRef.current = monaco;
    };
    const handleSQLChange = useCallback((value) => {
        setSqlQuery(value || '');
    }, []);
    const handleRunQuery = useCallback(async () => {
        if (!sqlQuery.trim()) {
            toast.addNotification({
                type: 'error',
                title: translate('Please enter a SQL query'),
            });
            return;
        }
        if (!selectedStudy?.value || !selectedEnvironment?.value) {
            toast.addNotification({
                type: 'error',
                title: translate('Please select a study and environment to run queries'),
            });
            return;
        }
        setIsRunningQuery(true);
        setQueryResult([]);
        try {
            let trimmedSql = sqlQuery.trim();
            // Append LIMIT 100 if no LIMIT is present
            const hasLimitAtEnd = /\bLIMIT\s+\d+\s*;?\s*$/i.test(trimmedSql);
            if (!hasLimitAtEnd && trimmedSql) {
                trimmedSql = trimmedSql.replace(/;\s*$/, '');
                trimmedSql = `${trimmedSql} LIMIT 100`;
            }
            const response = await webClient.post(`/api/data_transfer/configs/query`, {
                sql: trimmedSql,
                explain: false,
                environmentId: selectedEnvironment.value,
                studyId: selectedStudy.value,
            });
            setQueryResult(response.data || []);
        }
        catch (error) {
            const axiosError = error;
            toast.addNotification({
                type: 'error',
                title: translate('Error running query'),
                subtitle: axiosError.response?.data?.message || translate('Failed to execute SQL query'),
            });
            setQueryResult([]);
        }
        finally {
            setIsRunningQuery(false);
        }
    }, [sqlQuery, selectedStudy?.value, selectedEnvironment?.value, webClient, toast, translate]);
    const columns = useMemo(() => {
        return queryResult.length > 0
            ? Object.keys(queryResult[0]).map((column) => ({
                header: column,
                accessorKey: column,
            }))
            : [];
    }, [queryResult]);
    const handleCreate = useCallback(() => {
        if (queryResult.length === 0 || columns.length === 0) {
            toast.addNotification({
                type: 'error',
                title: translate('Please run a query first to create dimensions'),
            });
            return;
        }
        // Extract dimensions from query results
        // Each column becomes a dimension
        const dimensions = columns.map((col) => {
            const columnName = col.header;
            // Infer type from first row's data
            const firstValue = queryResult[0]?.[columnName];
            let inferredType = 'string';
            if (typeof firstValue === 'number') {
                inferredType = 'number';
            }
            else if (firstValue instanceof Date ||
                (typeof firstValue === 'string' && /^\d{4}-\d{2}-\d{2}/.test(firstValue))) {
                inferredType = 'time';
            }
            else if (typeof firstValue === 'boolean') {
                inferredType = 'boolean';
            }
            return {
                name: columnName.toLowerCase().replace(/\s+/g, '_'),
                type: inferredType,
                sql: columnName, // This will need to be the actual SQL expression from the query
                title: columnName,
            };
        });
        onSave(dimensions, sqlQuery);
    }, [queryResult, columns, onSave, sqlQuery, toast, translate]);
    const createButton = {
        label: translate(isEditMode ? 'Save' : 'Create'),
        onClick: handleCreate,
        disabled: isLoading || queryResult.length === 0 || columns.length === 0,
    };
    const cancelButton = {
        label: translate('Cancel'),
        onClick: onClose,
    };
    if (!isOpen) {
        return null;
    }
    return (_jsx(ModalWindow, { title: translate(isEditMode ? 'Edit dimensions' : 'Create dimensions'), isOpen: true, closeWindow: onClose, footerPrimaryActionButton: createButton, footerSecondaryActionButtons: [cancelButton], width: 'full', children: _jsxs("div", { className: 'grid grid-cols-2 gap-4 size-full min-h-[500px]', children: [_jsxs("div", { className: 'space-y-4 size-full flex flex-col min-w-0', children: [_jsxs("div", { className: 'flex items-center justify-between', children: [_jsx("div", { children: translate('Editor') }), _jsxs("div", { className: 'flex items-center gap-4', children: [_jsx(Select, { placeholder: translate('Select a study'), options: studyOptions, value: selectedStudy, onChange: handleStudyChange, width: 'full', searchableOptions: true }), _jsx(Select, { placeholder: translate('Select an environment'), options: environmentOptions, value: selectedEnvironment, onChange: handleEnvironmentChange, width: 'full', disabled: !selectedStudy?.value || isLoadingEnvironments, searchableOptions: true }), _jsx(Button, { label: translate('Run'), onClick: handleRunQuery, disabled: !sqlQuery.trim() || isRunningQuery, variant: 'primary' })] })] }), _jsx("div", { className: 'border rounded-lg flex-1 py-4', children: _jsx(Editor, { height: '100%', defaultLanguage: 'sql', value: sqlQuery, onChange: handleSQLChange, onMount: handleEditorDidMount, options: {
                                    minimap: { enabled: false },
                                    fontSize: 14,
                                    formatOnType: true,
                                    lineNumbers: 'on',
                                    automaticLayout: true,
                                } }) })] }), _jsxs("div", { className: 'border-l px-4 space-y-4 min-w-0 overflow-hidden flex flex-col', children: [_jsx("div", { className: 'text-md font-semibold', children: translate('Data preview') }), _jsx("div", { className: 'overflow-auto flex-1', children: queryResult.length > 0 || isRunningQuery ? (_jsx(Table, { columns: columns, data: queryResult, isLoading: isRunningQuery })) : (_jsx("div", { className: 'flex items-center justify-center h-full', children: _jsx("div", { className: 'text-center text-text-secondary', children: translate('Select a study and environment, then click Run to preview data.') }) })) })] })] }) }));
};
